<template>
  <div v-if="configuredProviders.length" class="space-y-3" :class="wrapperClass">
    <div v-if="showDividerAbove" class="relative flex items-center py-1">
      <div class="flex-grow border-t border-gray-200"></div>
      <span class="flex-shrink mx-3 text-xs text-gray-400 uppercase tracking-wide">{{ dividerLabel }}</span>
      <div class="flex-grow border-t border-gray-200"></div>
    </div>

    <button
      v-for="provider in configuredProviders"
      :key="provider.id"
      :ref="(el) => setProviderButtonRef(provider.id, el)"
      type="button"
      class="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-md border border-gray-300 transition duration-300 ease-in-out flex items-center justify-center space-x-3 group"
      :class="{
        'ring-2 ring-teal-500 border-teal-500 shadow-sm': highlightProviderId === provider.id,
        'shadow-sm': variant === 'register',
      }"
      :disabled="disabled"
      @click="onProviderClick(provider.id)"
    >
      <!-- Google -->
      <svg v-if="provider.id === 'google'" class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>

      <!-- ClassLink -->
      <svg v-else-if="provider.id === 'classlink'" class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
        <rect width="24" height="24" rx="4" fill="#1B75BB"/>
        <text x="12" y="16.5" text-anchor="middle" fill="#FFFFFF" font-size="11" font-family="Arial, Helvetica, sans-serif" font-weight="700">CL</text>
      </svg>

      <font-awesome-icon v-else icon="fa-regular fa-link" class="w-5 h-5 text-slate-500" />

      <span>{{ buttonLabel(provider) }}</span>
    </button>

    <p v-if="ssoRequired && highlightLabel" class="text-sm text-teal-700 text-center">
      Continue with {{ highlightLabel }} to sign in to this account.
    </p>

    <div v-if="showDividerBelow" class="relative flex items-center py-1">
      <div class="flex-grow border-t border-gray-200"></div>
      <span class="flex-shrink mx-3 text-xs text-gray-400 uppercase tracking-wide">{{ dividerLabel }}</span>
      <div class="flex-grow border-t border-gray-200"></div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  fetchSsoProviders,
  getConfiguredSsoProviders,
  getSafeReturnTo,
  getSsoProviderLabel,
  startSsoLogin,
} from '@/utils/ssoAuth'

export default {
  name: 'SsoProviderButtons',
  props: {
    /** 'login' | 'register' — affects button copy */
    mode: {
      type: String,
      default: 'login',
    },
    returnTo: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ssoRequired: {
      type: Boolean,
      default: false,
    },
    /** When ssoRequired, optionally highlight a specific provider id */
    highlightProviderId: {
      type: String,
      default: '',
    },
    dividerLabel: {
      type: String,
      default: 'or',
    },
    /** Place divider above buttons (login) or below (register) */
    dividerPosition: {
      type: String,
      default: 'above', // 'above' | 'below' | 'none'
    },
    wrapperClass: {
      type: String,
      default: '',
    },
  },
  setup (props) {
    const route = useRoute()
    const ssoProviders = ref([])
    const buttonRefs = ref({})

    const configuredProviders = computed(() => getConfiguredSsoProviders(ssoProviders.value))
    const showDividerAbove = computed(() => props.dividerPosition === 'above' && configuredProviders.value.length > 0)
    const showDividerBelow = computed(() => props.dividerPosition === 'below' && configuredProviders.value.length > 0)
    const variant = computed(() => props.mode === 'register' ? 'register' : 'login')
    const highlightLabel = computed(() => {
      const id = String(props.highlightProviderId || '').toLowerCase()
      if (id) return getSsoProviderLabel(id)
      if (configuredProviders.value.length === 1) return configuredProviders.value[0].label
      return 'SSO'
    })

    const buttonLabel = (provider) => {
      if (props.mode === 'register') return `Sign up with ${provider.label}`
      if (provider.id === 'classlink') {
        return `Continue with ${provider.label}`
      }
      return `Sign in with ${provider.label}`
    }

    const setProviderButtonRef = (id, el) => {
      if (el) buttonRefs.value[id] = el
      else delete buttonRefs.value[id]
    }

    const onProviderClick = (providerId) => {
      if (props.disabled) return
      const returnTo = getSafeReturnTo(props.returnTo || route.fullPath || '/dashboard')
      startSsoLogin(providerId, { returnTo })
    }

    watch(
      () => props.ssoRequired,
      async (required) => {
        if (!required) return
        await nextTick()
        const id = String(props.highlightProviderId || configuredProviders.value[0]?.id || '').toLowerCase()
        const el = buttonRefs.value[id]
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
      }
    )

    fetchSsoProviders().then((list) => {
      ssoProviders.value = list
    })

    return {
      configuredProviders,
      showDividerAbove,
      showDividerBelow,
      variant,
      highlightLabel,
      buttonLabel,
      setProviderButtonRef,
      onProviderClick,
    }
  },
}
</script>
